// Imports ------------------------------------------------------------------->

import {inject} from "../di";
import {ConfigService} from "../ConfigService";
import {IAuthenticationService, AuthUser, AuthProviderUser, AuthStatus} from "../definitions/IAuthenticationService";
import {AuthProvider, PostMessageEvent} from "../../definitions/CommonDefinitions";
import {fetchJSON} from "../../utils/fetch";

// Private Definitions ------------------------------------------------------->

const OAUTH_URL = "https://www.facebook.com/v2.8/dialog/oauth";
const USER_PROFILE = "https://graph.facebook.com/me";

// Public Definitions -------------------------------------------------------->

export class FacebookAuthenticator implements IAuthenticationService {
	@inject config: ConfigService;

	/* FIXME: There is some overlap in functionality between this class and GoogleAuthenticator.ts (eg: loginPromise,
		*        active login window, etc). Consider abstracting this into a common base class */
	private userInfo: AuthProviderUser;
	private loginPromise: Promise<void>;
	private activePopup: Window;

	get status() {
		return this.userInfo ? AuthStatus.LOGGED_IN : AuthStatus.LOGGED_OUT;
	}

	get user() {
		return {
			info: this.userInfo
		} as AuthUser;
	}

	init = () => {
		return new Promise<void>((resolve, reject) => {
			const loginCookie = Cookies.get("flipp-user");
			if (loginCookie) {
				const data = JSON.parse(loginCookie);
				this.userInfo = data;

				/* Don't automatically log the user out when their FB token expires.
					* See: FW-712: User should stay logged in until they log out
					* Keeping the code below as reference as we may re-introduce it as part of redemption work
					*/
				// const now = new Date().getTime();
				// if (now > data.expiresAt) {
				//   Cookies.remove("flipp-login");
				//   Cookies.remove("flipp-user");
				// } else {
				// }
			}
			resolve();
		});
	}

	login = () => {
		/* If login already exists, return existing login Promise and focus the open window */
		if (this.loginPromise) {
			if (this.activePopup) {
				this.activePopup.focus();
			}
			return this.loginPromise;
		}

		let windowOpenCheck: number;
		this.loginPromise = new Promise((resolve, reject) => {
			/* Construct oauth URL */
			const redirectUrl = `${window.location.protocol}//${window.location.host}/login_redirect`;
			const state = JSON.stringify({
				provider: AuthProvider.FACEBOOK,
				path: window.location.pathname
			});
			const authUrl = `${OAUTH_URL}?client_id=${this.config.FACEBOOK_APP_ID}&redirect_uri=${redirectUrl}&state=${encodeURIComponent(state)}&response_type=token&scope=email,public_profile&display=popup`;

			/* Setup listener for "postMessage" events */
			const receiveMessage = (evt: PostMessageEvent) => {
				const message = evt.data;
				if (message.result !== "success" || evt.origin !== window.location.origin) {
					reject(new Error());
					return;
				}

				/* Message is valid at this point - do cleanup */
				window.removeEventListener("message", receiveMessage);
				this._resetLogin();
				clearInterval(windowOpenCheck);
				windowOpenCheck = -1;

				/* Fetch the user profile */
				this._fetchUserProfile(message.data.access_token).then((profile: any) => {
					this.userInfo = {
						id: "",
						name: profile ? profile.name : null,
						image: profile ? `https://graph.facebook.com/${profile.id}/picture` : null,
						email: profile ? profile.email : null,
						accessToken: message.data.access_token,
						expiresAt: new Date().getTime() + Number(message.data.expires_in) * 1000,
						provider: AuthProvider.FACEBOOK
					};

					/* Store the auth values in a Cookie instead of localStorage because some Browser modes (eg: Safari Private)
						* prevent access to localStorage */
					Cookies.set("flipp-user", JSON.stringify(this.userInfo), {
						expires: 30,
						// domain: this.config.getCookiesDomain()
					});
					resolve();
				});
			};
			window.addEventListener("message", receiveMessage);

			/* Open oauth URL in popup */
			const width = 550;
			const height = 300;
			const left = (screen.width / 2) - (width / 2);
			const top = (screen.height / 2) - (height / 2);
			this.activePopup = window.open(authUrl, "Flipp", `height=${height},width=${width},top=${top},left=${left}`);
			if (this.activePopup.focus) {
				this.activePopup.focus();
			}

			/* Poll to see if Login window is closed, and call reject() if it is */
			windowOpenCheck = setInterval(() => {
				if (!this.activePopup || this.activePopup.closed) {
					this._resetLogin();
					window.removeEventListener("message", receiveMessage);
					clearInterval(windowOpenCheck);
					windowOpenCheck = -1;
					reject(new Error());
				}
			}, 3000);
		});

		return this.loginPromise;
	}

	logout = () => {
		return Promise.resolve().then(() => {
			this.userInfo = null;
			Cookies.remove("flipp-user");
		});
	}

	private _resetLogin = () => {
		this.activePopup = null;
		this.loginPromise = null;
	}

	private _fetchUserProfile = (token: string) => {
		const url = `${USER_PROFILE}?fields=name,email&access_token=${token}`;
		return fetchJSON(url).then((profile) => {
			return profile;
		}).catch((e: Error) => {
			/* An error here is not fatal - simply catch it and return null. The UI will not be completely
				* populated (eg: no user image), but we have the email from Accounts API and that's enough.
				* Firefox Private mode blocks ALL access to the Facebook Graph API and so this error is EXPECTED
				* in that scenario. */
			return null;
		});
	}
}
